import { Box, Container, Grid, Header, Icon, SpaceBetween, TableProps, Toggle } from '@amzn/awsui-components-core';
import React, { useEffect, useState } from 'react';
import { useFinTechContext } from 'src/components/context/FintechContextProvider';
import { LoadingSpinner } from 'src/components/generic-components/LoadingSpinner';
import { numberFormatter } from 'src/utilities/CommonUtilities';
import { FinOpsTable } from '../fintech-ops-table/FinOpsTable';
import { indicatorIcon, indicatorIconVariant } from '../fintech-ops-dashboard/FinTechOpsUtils';
import { ErrorContainer, NotInitializedContainer } from '../FinTechOpsCommonElements';
import styles from './styles.module.scss';
import { useUserMetricsContext } from './UserMetricsContext';
import { Top3Apps } from './UserMetricsInterfaces';
import {
  USER_METRICS_APPLICATION_USER_VIEW__FILTERING_PROPERTIES,
  USER_METRICS_FOOTPRINT_FILTERING_PROPERTIES,
  USER_METRICS_SUB_APPLICATION_FOOTPRINT_FILTERING_PROPERTIES,
  USER_METRICS_SUB_APPLICATION_USER_VIEW__FILTERING_PROPERTIES
} from './UserMetricsTableFilterConfig';

export const UserMetricsDetailsTab: React.FC = () => {
  const finOpsContext = useFinTechContext();
  const userMetricsContext = useUserMetricsContext();

  const [checked, setChecked] = useState(false);
  const [applicationChecked, setApplicationChecked] = useState(false);

  const [footprintChecked, setFootprintChecked] = useState(false);
  const [userViewChecked, setUserViewChecked] = useState(false);

  // Top 3 Apps
  const top3AppVisibleColumns = ['application', 'views', 'MoM'];
  const [top3AppColumnDefinition, setTop3AppColumnDefinition] = useState<TableProps.ColumnDefinition<any>[]>([]);
  const [top3AppData, setTop3AppData] = useState<any[]>([]);

  useEffect(() => {
    const currentMonth = userMetricsContext.applicationFootprintContainer.currentMonth;
    if (userMetricsContext.applicationFootprintContainer.top3Apps) {
      const colDef: TableProps.ColumnDefinition<Top3Apps>[] = [
        {
          id: 'application',
          header: 'Top 3',
          sortingField: 'application',
          cell: (item: Top3Apps) => <>{item.application}</>,
          width: '120px'
        },
        {
          id: 'views',
          header: 'Views ' + currentMonth,
          sortingField: 'views',
          cell: (item: Top3Apps) => <>{numberFormatter(item.views) || '-'}</>,
          width: '160px'
        },
        {
          id: 'MoM',
          header: 'MoM',
          sortingField: 'MoM',
          cell: (item: Top3Apps) => (
            <>
              {numberFormatter(Math.abs(item.MoM)) || '-'}
              <Icon name={indicatorIcon(item.MoM)} variant={indicatorIconVariant(item.MoM ? item.MoM * -1 : 0)} />
            </>
          )
        }
      ];
      setTop3AppColumnDefinition(colDef);
    }
    setTop3AppData(userMetricsContext.applicationFootprintContainer.top3Apps || []);
  }, [userMetricsContext.applicationFootprintContainer.top3Apps]);

  return (
    <div>
      {finOpsContext.finTechOpsUserMetricsDataStatus == 'loading' && <LoadingSpinner />}
      {finOpsContext.finTechOpsUserMetricsDataStatus == 'error' && <ErrorContainer />}
      {finOpsContext.finTechOpsUserMetricsDataStatus == 'pending' && <NotInitializedContainer />}
      {finOpsContext.finTechOpsUserMetricsDataStatus == 'finished' && (
        <Grid
          gridDefinition={[
            { colspan: { default: 12, xxs: 12, xs: 12, s: 12, m: 4, l: 4, xl: 4 } },
            { colspan: { default: 12, xxs: 12, xs: 12, s: 12, m: 8, l: 8, xl: 8 } },
            { colspan: { default: 12, xxs: 12, xs: 12, s: 12, m: 12, l: 12, xl: 12 } },
            { colspan: { default: 12, xxs: 12, xs: 12, s: 12, m: 12, l: 12, xl: 12 } }
          ]}
        >
          <Container fitHeight header={<Header variant="h2">Total Sub Application Views</Header>}>
            <Box variant="h3" textAlign="center">
              {numberFormatter(userMetricsContext.applicationFootprintContainer.totalViews)}
            </Box>
            {userMetricsContext.applicationFootprintContainer.top3Apps.length > 0 ? (
              <div className={styles.UserMetricsFootPrintContainerTable}>
                <FinOpsTable
                  pageSize={3}
                  stickyHeader={false}
                  emptyMessage="No costMetrics data to display"
                  filterProperties={[]}
                  visibleContent={top3AppVisibleColumns}
                  columnDefinitions={top3AppColumnDefinition}
                  data={top3AppData}
                />
              </div>
            ) : (
              <Box textAlign="center">No data To Display</Box>
            )}
          </Container>
          <Container
            fitHeight
            header={
              <>
                <SpaceBetween size="m" direction="horizontal" alignItems="center">
                  <Header>Aggregated Metrics by Application</Header>
                  <Toggle
                    onChange={({ detail }) => {
                      setChecked(detail.checked);
                    }}
                    checked={checked}
                  >
                    {!checked ? 'Unique Users' : 'Views'}
                  </Toggle>

                  <Toggle
                    onChange={({ detail }) => {
                      setApplicationChecked(detail.checked);
                    }}
                    checked={applicationChecked}
                  >
                    {!applicationChecked ? 'Sub Application' : 'Application'}
                  </Toggle>
                </SpaceBetween>
              </>
            }
          >
            {checked && applicationChecked ? (
              <div className={styles.UserMetricsAggregatedDetailsTable}>
                <FinOpsTable
                  pageSize={6}
                  emptyMessage="No User Metrics to Display"
                  filterProperties={[]}
                  visibleContent={userMetricsContext.aggregatedViewsTableData.visibleColumns}
                  columnDefinitions={userMetricsContext.aggregatedViewsTableData.columnData}
                  data={userMetricsContext.aggregatedViewsTableData.rowData}
                />
              </div>
            ) : checked && !applicationChecked ? (
              <div className={styles.UserMetricsAggregatedDetailsTable}>
                <FinOpsTable
                  pageSize={100}
                  emptyMessage="No User Metrics to Display"
                  filterProperties={[]}
                  visibleContent={userMetricsContext.aggregatedSubApplicationViewsTableData.visibleColumns}
                  columnDefinitions={userMetricsContext.aggregatedSubApplicationViewsTableData.columnData}
                  data={userMetricsContext.aggregatedSubApplicationViewsTableData.rowData}
                />
              </div>
            ) : !checked && applicationChecked ? (
              <div className={styles.UserMetricsAggregatedDetailsTable}>
                <FinOpsTable
                  pageSize={100}
                  emptyMessage="No User Metrics to Display"
                  filterProperties={[]}
                  visibleContent={userMetricsContext.applicationUsersTableData.visibleColumns}
                  columnDefinitions={userMetricsContext.applicationUsersTableData.columnData}
                  data={userMetricsContext.applicationUsersTableData.rowData}
                />
              </div>
            ) : !checked && !applicationChecked ? (
              <div className={styles.UserMetricsAggregatedDetailsTable}>
                <FinOpsTable
                  pageSize={100}
                  emptyMessage="No User Metrics to Display"
                  filterProperties={[]}
                  visibleContent={userMetricsContext.aggregatedSubApplicationUniqueUsersTableData.visibleColumns}
                  columnDefinitions={userMetricsContext.aggregatedSubApplicationUniqueUsersTableData.columnData}
                  data={userMetricsContext.aggregatedSubApplicationUniqueUsersTableData.rowData}
                />
              </div>
            ) : (
              ''
            )}
          </Container>
          {/* Footprint Details */}
          <Container
            fitHeight
            header={
              <>
                <SpaceBetween size="m" direction="horizontal" alignItems="center">
                  <Header>Footprint Details</Header>
                  <Toggle
                    onChange={({ detail }) => {
                      setFootprintChecked(detail.checked);
                    }}
                    checked={footprintChecked}
                  >
                    {!footprintChecked ? 'Sub Application' : 'Application'}
                  </Toggle>
                </SpaceBetween>
              </>
            }
          >
            {footprintChecked ? (
              <div className={styles.UserMetricsFootPrintDetailsTable}>
                <FinOpsTable
                  pageSize={10}
                  emptyMessage="No User Metrics to Display"
                  filterProperties={USER_METRICS_FOOTPRINT_FILTERING_PROPERTIES}
                  visibleContent={userMetricsContext.applicationFootprintTableData.visibleColumns}
                  columnDefinitions={userMetricsContext.applicationFootprintTableData.columnData}
                  data={userMetricsContext.applicationFootprintTableData.rowData}
                />
              </div>
            ) : (
              <div className={styles.UserMetricsFootPrintDetailsTable}>
                <FinOpsTable
                  pageSize={10}
                  emptyMessage="No User Metrics to Display"
                  filterProperties={USER_METRICS_SUB_APPLICATION_FOOTPRINT_FILTERING_PROPERTIES}
                  visibleContent={userMetricsContext.subApplicationFootprintTableData.visibleColumns}
                  columnDefinitions={userMetricsContext.subApplicationFootprintTableData.columnData}
                  data={userMetricsContext.subApplicationFootprintTableData.rowData}
                />
              </div>
            )}
          </Container>
          {/* User View */}
          <Container
            fitHeight
            header={
              <>
                <SpaceBetween size="m" direction="horizontal" alignItems="center">
                  <Header>User View</Header>
                  <Toggle
                    onChange={({ detail }) => {
                      setUserViewChecked(detail.checked);
                    }}
                    checked={userViewChecked}
                  >
                    {userViewChecked ? 'Application' : 'Sub Application'}
                  </Toggle>
                </SpaceBetween>
              </>
            }
          >
            {userViewChecked ? (
              <FinOpsTable
                pageSize={10}
                emptyMessage="No User Metrics to Display"
                filterProperties={USER_METRICS_APPLICATION_USER_VIEW__FILTERING_PROPERTIES}
                visibleContent={userMetricsContext.applicationUserViewTableData.visibleColumns}
                columnDefinitions={userMetricsContext.applicationUserViewTableData.columnData}
                data={userMetricsContext.applicationUserViewTableData.rowData}
                enableDownloadReport={true}
                downloadFileName="User_View_Application_Level"
              />
            ) : (
              <FinOpsTable
                pageSize={10}
                emptyMessage="No User Metrics to Display"
                filterProperties={USER_METRICS_SUB_APPLICATION_USER_VIEW__FILTERING_PROPERTIES}
                visibleContent={userMetricsContext.subApplicationUserViewTableData.visibleColumns}
                columnDefinitions={userMetricsContext.subApplicationUserViewTableData.columnData}
                data={userMetricsContext.subApplicationUserViewTableData.rowData}
                enableDownloadReport={true}
                downloadFileName="User_View_Sub_Application_Level"
              />
            )}
          </Container>
        </Grid>
      )}
    </div>
  );
};
